import * as XLSX from "xlsx";

const EXPECTED_HEADERS = [
  "date",
  "withdrawls",
  "deposits",
  "payee",
  "description",
  "reference number",
];

const HEADER_ALIASES: Record<string, string> = {
  withdrawals: "withdrawls",
};

const COL_DATE = 0;
const COL_WITHDRAWLS = 1;
const COL_DEPOSITS = 2;
const COL_DESCRIPTION = 4;

export interface StatementExcelValidationResult {
  valid: boolean;
  message?: string;
}

function normalizeHeader(value: unknown): string {
  const s = String(value ?? "")
    .trim()
    .toLowerCase();
  return HEADER_ALIASES[s] ?? s;
}

function isEmptyRow(row: unknown[]): boolean {
  return row.every(
    (cell) => cell === null || cell === undefined || String(cell).trim() === ""
  );
}

function parseAmount(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const s = String(value).replace(/,/g, "").trim();
  if (s === "") return null;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

/**
 * Client-side validation of bank statement Excel format.
 * Row 1 must be: Date, Withdrawls, Deposits, Payee, Description, Reference Number.
 * Each data row must have Date, Description, and either Withdrawls or Deposits (non-empty and numeric).
 */
export async function validateStatementExcel(
  file: File
): Promise<StatementExcelValidationResult> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: "array" });
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) {
      return { valid: false, message: "Excel file has no sheets." };
    }
    const sheet = workbook.Sheets[sheetName];
    const rows: unknown[][] = XLSX.utils.sheet_to_json(sheet, {
      header: 1,
      defval: "",
    });

    if (!rows.length) {
      return { valid: false, message: "Excel file has no data." };
    }

    const headerRow = rows[0];
    const normalizedHeaders = headerRow.slice(0, 6).map(normalizeHeader);

    if (normalizedHeaders.length < EXPECTED_HEADERS.length) {
      return {
        valid: false,
        message:
          "Invalid Excel format. Row 1 must contain: Date, Withdrawls, Deposits, Payee, Description, Reference Number. Please download the template.",
      };
    }

    for (let c = 0; c < EXPECTED_HEADERS.length; c++) {
      if (normalizedHeaders[c] !== EXPECTED_HEADERS[c]) {
        return {
          valid: false,
          message:
            "Invalid Excel format. Row 1 must contain (in order): Date, Withdrawls, Deposits, Payee, Description, Reference Number. Please download the template.",
        };
      }
    }

    for (let i = 1; i < rows.length; i++) {
      const row = rows[i] as unknown[];
      if (!Array.isArray(row)) continue;
      if (isEmptyRow(row)) continue;

      const date = String(row[COL_DATE] ?? "").trim();
      const description = String(row[COL_DESCRIPTION] ?? "").trim();
      const withdrawlsVal = row[COL_WITHDRAWLS];
      const depositsVal = row[COL_DEPOSITS];
      const withdrawlsNum = parseAmount(withdrawlsVal);
      const depositsNum = parseAmount(depositsVal);
      const hasWithdrawls =
        withdrawlsVal !== null &&
        withdrawlsVal !== undefined &&
        String(withdrawlsVal).trim() !== "" &&
        withdrawlsNum !== null;
      const hasDeposits =
        depositsVal !== null &&
        depositsVal !== undefined &&
        String(depositsVal).trim() !== "" &&
        depositsNum !== null;

      if (date === "") {
        return {
          valid: false,
          message: `Row ${
            i + 1
          } is invalid: Date, Description, and either Withdrawls or Deposits are required.`,
        };
      }
      if (description === "") {
        return {
          valid: false,
          message: `Row ${
            i + 1
          } is invalid: Date, Description, and either Withdrawls or Deposits are required.`,
        };
      }
      if (!hasWithdrawls && !hasDeposits) {
        return {
          valid: false,
          message: `Row ${
            i + 1
          } is invalid: Date, Description, and either Withdrawls or Deposits are required.`,
        };
      }
    }

    return { valid: true };
  } catch (e) {
    console.error("validateStatementExcel error:", e);
    return {
      valid: false,
      message:
        "Failed to read Excel file. Ensure it is a valid .xls or .xlsx file with the correct format.",
    };
  }
}
